import { useEffect, useRef } from 'react';

import { subscribe } from '@/services/realtime/client';
import { env } from '@/util/env';
import { Sentry } from '@/util/sentry';

export type RealtimeConnection = 'connecting' | 'live' | 'down' | 'refused';

export type RealtimeChannelMonitoring = {
  breadcrumbCategory: string;
  refusedMessage: string;
  component: string;
  source: string;
};

export type UseRealtimeChannelOptions<T> = {
  enabled: boolean;
  /**
   * Who the subscription is for. A change closes the socket and opens a new
   * one, so switching the acted-as account follows the new account's channel.
   */
  identity: string | undefined;
  getToken: () => Promise<string>;
  /**
   * The channel to follow, derived from the token the socket authenticates
   * with, or null when the token names no one, which counts as refused.
   */
  channelFor: (token: string) => string | null;
  parse: (raw: unknown) => T;
  onEvent: (event: T) => void;
  onResume: () => void;
  onConnectionChange: (connection: RealtimeConnection) => void;
  monitoring: RealtimeChannelMonitoring;
};

/**
 * Holds one subscription to a realtime channel while `enabled`, and reports
 * its state.
 *
 * The socket is only an accelerator. Its events are hints for the caller to
 * re-read from the API, `onResume` fires after every reconnect because the
 * channel replays nothing, and the caller polls or refetches on focus while
 * the connection is anything but `live`. An unauthorized refusal is reported
 * to Sentry once and never retried; transient errors are breadcrumbs only.
 *
 * The channel is taken from the token the socket connects with rather than
 * from app state, because the server authorizes the channel against that
 * token: a channel derived from anything else can be refused.
 *
 * Every callback is read through a ref, so a caller passing new functions each
 * render does not reopen the socket. Only `enabled` and `identity` do.
 */
export function useRealtimeChannel<T>(options: UseRealtimeChannelOptions<T>): void {
  const { enabled, identity } = options;

  const optionsRef = useRef(options);

  optionsRef.current = options;

  useEffect(() => {
    const current = () => optionsRef.current;

    if (!enabled) {
      current().onConnectionChange('down');

      return undefined;
    }

    let cancelled = false;

    let unsubscribe: (() => void) | undefined;

    current().onConnectionChange('connecting');

    current()
      .getToken()
      .then((token) => {
        if (cancelled) {
          return;
        }

        const channel = current().channelFor(token);

        if (!channel) {
          current().onConnectionChange('refused');

          return;
        }

        unsubscribe = subscribe({
          host: env.EXPO_PUBLIC_REALTIME_HOST,
          channel,
          getToken: () => current().getToken(),
          parse: (raw) => current().parse(raw),
          onEvent: (event) => current().onEvent(event),
          onSubscribed: ({ resumed }) => {
            current().onConnectionChange('live');

            if (resumed) {
              current().onResume();
            }
          },
          onDisconnected: () => current().onConnectionChange('down'),
          onError: (_error, { fatal }) => {
            const { monitoring, onConnectionChange } = current();

            Sentry.addBreadcrumb({
              category: monitoring.breadcrumbCategory,
              level: fatal ? 'error' : 'info',
              message: fatal ? 'Realtime subscription refused' : 'Realtime connection error',
            });

            if (fatal) {
              onConnectionChange('refused');

              Sentry.captureMessage(monitoring.refusedMessage, {
                level: 'warning',
                tags: { component: monitoring.component, source: monitoring.source },
              });
            }
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          current().onConnectionChange('down');
        }
      });

    return () => {
      cancelled = true;

      unsubscribe?.();
    };
  }, [enabled, identity]);
}
